import { useEffect, useCallback } from 'react';
import { Howl } from 'howler';
import { atom, useAtom } from 'jotai';
import { bgmVolumeStorage } from '~/core/storages';

type BgmTheme = 'day' | 'night';
const DEFAULT_BGM_VOLUME = 0.3;
const clampVolume = (value: number) => Math.max(0, Math.min(1, value));

const tracksRef: { day: Howl | null; night: Howl | null } = { day: null, night: null };
const desiredThemeRef = { current: 'day' as BgmTheme };
const volumeRef = { current: DEFAULT_BGM_VOLUME };
const mutedRef = { current: true }; // demo: background music off by default
let subscribers = 0;
let hasLoadedStoredVolume = false;

const atomBgmVolume = atom(DEFAULT_BGM_VOLUME);
const atomBgmMuted = atom(true); // demo: background music off by default
const atomBgmTheme = atom<BgmTheme>('day');

const forEachTrack = (fn: (track: Howl) => void) => {
  Object.values(tracksRef).forEach(track => {
    if (track) fn(track);
  });
};

const applyTheme = () => {
  const desired = desiredThemeRef.current;
  const target = tracksRef[desired];
  const other = tracksRef[desired === 'day' ? 'night' : 'day'];

  if (other) {
    try { other.stop(); } catch {}
  }
  if (!target) return;

  const start = () => {
    if (desiredThemeRef.current !== desired) return;
    target.volume(volumeRef.current);
    target.mute(mutedRef.current);
    if (!target.playing()) {
      target.play();
    }
  };

  if (target.state() === 'loaded') {
    start();
  } else {
    target.once('load', start);
  }
};

export const useBGM = () => {
  const [volume, setVolumeAtom] = useAtom(atomBgmVolume);
  const [isMuted, setIsMuted] = useAtom(atomBgmMuted);
  const [theme, setThemeAtom] = useAtom(atomBgmTheme);
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  useEffect(() => {
    subscribers += 1;

    if (!hasLoadedStoredVolume) {
      hasLoadedStoredVolume = true;
      const stored = bgmVolumeStorage.get();
      if (stored) {
        const parsed = clampVolume(parseFloat(stored));
        setVolumeAtom(parsed);
      }
    }

    if (!tracksRef.day && !tracksRef.night) {
      tracksRef.day = new Howl({ src: ['/blinko/bgm-day.mp3'], loop: true, volume: volumeRef.current, preload: true });
      tracksRef.night = new Howl({ src: ['/blinko/bgm-night.mp3'], loop: true, volume: volumeRef.current, preload: true });
      applyTheme();
    }

    return () => {
      subscribers -= 1;
      if (subscribers === 0) {
        forEachTrack(track => track.unload());
        tracksRef.day = null;
        tracksRef.night = null;
      }
    };
  }, [setVolumeAtom]);

  useEffect(() => {
    desiredThemeRef.current = theme;
    applyTheme();
  }, [theme]);

  useEffect(() => {
    volumeRef.current = volume;
    bgmVolumeStorage.set(volume.toString());
    forEachTrack(track => track.volume(volume));
  }, [volume]);

  useEffect(() => {
    mutedRef.current = isMuted;
    forEachTrack(track => track.mute(isMuted));
  }, [isMuted]);

  useEffect(() => {
    const handler = () => {
      const shouldMute = document.hidden || mutedRef.current;
      forEachTrack(track => track.mute(shouldMute));
    };

    document.addEventListener('visibilitychange', handler);
    return () => {
      document.removeEventListener('visibilitychange', handler);
    };
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev);
  }, [setIsMuted]);

  const setTheme = useCallback((nextTheme: BgmTheme) => {
    if (nextTheme !== 'day' && nextTheme !== 'night') return;
    setThemeAtom(nextTheme);
  }, [setThemeAtom]);

  const setVolume = useCallback((value: number) => {
    const nextVolume = clampVolume(value);
    setIsMuted(false);
    setVolumeAtom(nextVolume);
  }, [setIsMuted, setVolumeAtom]);

  return { isMuted, toggleMute, setTheme, volume, setVolume };
};
